import {useDispatch, useSelector} from "react-redux";
import React, {useCallback, useRef, useState} from "react";
import {search} from "../../SearchActions";
import {useAutoFocus} from "../../../../common/useAutoFocus";
import {Button, Checkbox, createTheme, createStyles, FilledInput,
    FormControl, FormControlLabel, InputAdornment, InputLabel,
    makeStyles, ThemeProvider, withStyles} from "@material-ui/core";
import {Search} from "@material-ui/icons";
import styles from "./VideoSearchInput.module.css";
import {AppState} from "../../../../root/RootReducer";
import PuiTypography from "../../../../components/elements/PuiTypography";
import CheckboxIcon from "../../../../common/components/images/CheckboxIcon";
import CheckboxCheckedIcon from "../../../../common/components/images/CheckboxCheckedIcon";
import useResponsive from "../../../../common/useResponsive";
import clsx from "clsx";

const useStyles = makeStyles(() =>
    createStyles({
        rootMobile: {
            width: "calc(100vw - 48px)",
            minHeight: "120px",
            position: "fixed",
            zIndex: 100,
            backgroundColor: "white"
        },
        rootTablet: {
            width: "calc(100vw - 72px)",
            minHeight: "100px",
            position: "fixed",
            zIndex: 100,
            backgroundColor: "white"
        },
        margin: {
            width: "calc(100vw - 116px)",
            minWidth: "1100px",
            position: "fixed",
            zIndex: 100,
            backgroundColor: "white",
            marginTop: "-15px",
            "& .MuiInputLabel-root.Mui-focused": {
                color: "rgba(0, 0, 0, 0.54)"
            },
            "& .MuiFilledInput-underline:after": {
                borderBottom: "2px solid rgba(0, 0, 0, 0.54)"
            }
        },
        marginMobile: {
            width: "calc(100vw - 48px)",
            minWidth: "calc(100vw - 48px)",
            marginTop: "25px"
        },
        marginTablet: {
            width: "calc(100vw - 72px)",
            minWidth: "calc(100vw - 72px)",
            marginTop: "-18px"
        },
        searchTitle: {
            fontSize: "24px"
        },
        searchTitleMobile: {
            fontSize: "16px"
        },
        searchTitleTablet: {
            fontSize: "20px"
        },
        searchInput: {
            height: "70px",
            fontSize: "28px",
            borderRadius: "3px"
        },
        searchInputMobile: {
            height: "50px",
            fontSize: "16px"
        },
        button: {
            fontSize: "1.2rem",
            fontWeight: "bold",
            borderRadius: "5px"
        },
        buttonMobile: {
            fontSize: "0.75rem"
        },
        buttonTablet: {
            fontSize: "1rem"
        },
        filterCheckBox: {
            marginRight: "20px"
        }
    })
);

const CustomizeButton = withStyles(() => ({
    root: {
        color: "var(--white)",
        backgroundColor: "var(--red)",
        "&:hover": {
            color: "var(--white)",
            backgroundColor: "rgb(193, 0, 25)"
        }
    }
}))(Button);

const theme = createTheme({
    palette: {
        action: {
            disabledBackground: "rgba(0, 0, 0, 0.12)"
        }
    }
});

export function VideoSearchInput() {
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement>(null);
    const classes = useStyles();

    const {userInfo} = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo
    }));

    const {isMobile, isTablet} = useResponsive();

    useAutoFocus(inputRef);

    const [input, setInput] = useState<string>("");
    const [changing, setChanging] = useState<boolean>(false);

    const [targets, setTargets] = useState<string[]>([
        "titleHighlights",
        "descriptionHighlights",
        // "presenterHighlights",
        // "commentHighlights",  TODO:コメントの公開・非公開機能を実装したら復活する予定
        "sceneHighlights"
    ]);
    const [searchTarget, setSearchTarget] = useState({
        titleHighlights: true,
        descriptionHighlights: true,
        presenterHighlights: false,
        commentHighlights: false,
        sceneHighlights: true
    });

    const handleSearch = useCallback(() => {
        if(userInfo) dispatch(search(userInfo.id, input, targets));
        setChanging(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, input, targets]);

    const handleCheck = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTarget({...searchTarget, [event.target.name]: event.target.checked});

        if (event.target.checked) {
            targets.push(event.target.name);
            if(userInfo) dispatch(search(userInfo.id, input, targets));
            setChanging(false);
        } else {
            setTargets(targets.filter((t) => t !== event.target.name));
            if(userInfo) dispatch(search(userInfo.id, input, targets.filter((t) => t !== event.target.name)));
            setChanging(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, searchTarget, targets, input]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (input !== event.target.value) {
            setInput(event.target.value);

            if (targets.length > 0) {
                setChanging(true);
            }
        }
    };

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && input && targets.length > 0) {
            if(userInfo) dispatch(search(userInfo.id, input, targets));
            setChanging(false);
        }

        if (e.key !== "Enter") {
            setInput(e.currentTarget.value);
            if (targets.length > 0) {
                setChanging(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, input, targets]);

    return (
        <div className={clsx({
            [classes.rootMobile]: isMobile,
            [classes.rootTablet]: isTablet
        })}
        >
            <FormControl 
                fullWidth 
                className={clsx(classes.margin, {
                    [classes.marginMobile]: isMobile,
                    [classes.marginTablet]: isTablet
                })} 
                variant="filled" 
                color="secondary"
            >
                <InputLabel 
                    htmlFor="searchInput" 
                    className={clsx({
                        [classes.searchTitleMobile]: isMobile,
                        [classes.searchTitleTablet]: isTablet,
                        [classes.searchTitle]: !isMobile && !isTablet
                    })}
                >
                    Search For...
                </InputLabel>
                <FilledInput
                    id="searchInput"
                    value={input}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    endAdornment={
                        <InputAdornment position="end">
                            <ThemeProvider theme={theme}>
                                <CustomizeButton
                                    variant="contained"
                                    color="secondary"
                                    disabled={!input || !changing}
                                    onClick={handleSearch}
                                    className={clsx(classes.button, {
                                        [classes.buttonMobile]: isMobile,
                                        [classes.buttonTablet]: isTablet
                                    })}
                                    startIcon={<Search style={{fontSize: isMobile? "20px" : "24px"}}/>}
                                >
                                    Find
                                </CustomizeButton>
                            </ThemeProvider>
                        </InputAdornment>
                    }
                    autoComplete={"off"}
                    ref={inputRef}
                    className={clsx(classes.searchInput, {
                        [classes.searchInputMobile]: isMobile
                    })}
                />
            </FormControl>
            <div className={styles.option}>
                <FormControlLabel
                    control={
                        <Checkbox
                            icon={<div className={styles.checkedIcon}><CheckboxIcon/></div>}
                            checkedIcon={<div className={styles.checkedIcon}><CheckboxCheckedIcon/></div>}
                            name="titleHighlights"
                            checked={searchTarget.titleHighlights}
                            onChange={handleCheck}
                        />
                    }
                    label={<PuiTypography bold={true}>Title</PuiTypography>}
                    className={classes.filterCheckBox}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            icon={<div className={styles.checkedIcon}><CheckboxIcon/></div>}
                            checkedIcon={<div className={styles.checkedIcon}><CheckboxCheckedIcon/></div>}
                            name="descriptionHighlights"
                            checked={searchTarget.descriptionHighlights}
                            onChange={handleCheck}
                        />
                    }
                    label={<PuiTypography bold={true}>Description</PuiTypography>}
                    className={classes.filterCheckBox}
                />
                {/* TODO:コメントの公開・非公開機能を実装したら復活する予定
                <FormControlLabel
                    control={
                        <Checkbox
                            icon={<div className={styles.checkedIcon}><CheckboxIcon /></div>}
                            checkedIcon={<div className={styles.checkedIcon}><CheckboxCheckedIcon /></div>}
                            name="commentHighlights"
                            checked={searchTarget.commentHighlights}
                            onChange={handleCheck}
                        />
                   }
                    label={<PuiTypography bold={true}>Comment</PuiTypography>}
                    className={classes.filterCheckBox}
                /> */}
                <FormControlLabel
                    control={
                        <Checkbox
                            icon={<div className={styles.checkedIcon}><CheckboxIcon/></div>}
                            checkedIcon={<div className={styles.checkedIcon}><CheckboxCheckedIcon/></div>}
                            name="sceneHighlights"
                            checked={searchTarget.sceneHighlights}
                            onChange={handleCheck}
                        />
                    }
                    label={<PuiTypography bold={true}>Scene</PuiTypography>}
                    className={classes.filterCheckBox}
                />
            </div>
        </div>
    );
}
